// Google Drive picker + recursive folder walk + concurrent download
// helpers for the DrivePickerPanel. Ports the legacy `frontend/
// app.jsx` Drive helpers (lines 1958-2296) to TS.
//
// Architecture: thin module of `fetch` wrappers + the Google
// Picker SDK shim. We hit Drive's REST API directly with the
// operator's access token; nothing goes through the InRange
// backend. The output of `downloadDriveFileAsBlob` is a `File`
// the caller hands to the existing /uploads/initiate -> S3 PUT ->
// /uploads/complete flow, so imported files end up alongside
// drag-dropped + Gmail-imported files with content_type tagged
// from the file's MIME (or the export target for Google-native
// types).
import { GOOGLE_API_KEY } from "./google-config";
import { type PickerDoc } from "./google-auth";

export type { PickerDoc };

// ---------------------------------------------------------------------- //
// Constants
// ---------------------------------------------------------------------- //
export const FOLDER_MIME = "application/vnd.google-apps.folder";
export const MAX_FOLDER_DEPTH = 5;

// Default per-file size cap. Configurable in the preview UI before
// the operator clicks Confirm. Anything larger is moved to the
// "too big" bucket.
//
// MUST stay <= MAX_UPLOAD_BYTES in src/inrange_agents/uploads.py
// (currently 25 MB). Files between this cap and the backend cap
// would pass FE preview, get a presigned PUT URL, upload to S3,
// then 413 at /uploads/complete - leaving an orphan S3 object and
// a confusing "complete failed" toast. Keep this number in
// lock-step with MAX_UPLOAD_BYTES; the dropdown options offered
// in the preview UI don't exceed it.
export const DEFAULT_SIZE_CAP_MB = 25;

// Google-native MIME types -> ordered list of export-format
// candidates. PDF is preferred because the agent's pypdf extractor
// reads it directly, but Google enforces a 10 MB hard cap on PDF
// export and large Docs / Sheets / Slides trip it
// (`exportSizeLimitExceeded`). When that happens we fall through
// to plain text / CSV which compresses far smaller - the agent's
// extractors operate on text anyway, so layout fidelity isn't lost.
const NATIVE_EXPORT_MAP: Record<string, { mime: string; ext: string }[]> = {
  "application/vnd.google-apps.document": [
    { mime: "application/pdf", ext: ".pdf" },
    { mime: "text/plain", ext: ".txt" },
  ],
  "application/vnd.google-apps.spreadsheet": [
    { mime: "application/pdf", ext: ".pdf" },
    { mime: "text/csv", ext: ".csv" },
  ],
  "application/vnd.google-apps.presentation": [
    { mime: "application/pdf", ext: ".pdf" },
    { mime: "text/plain", ext: ".txt" },
  ],
  "application/vnd.google-apps.drawing": [
    { mime: "application/pdf", ext: ".pdf" },
  ],
  "application/vnd.google-apps.script": [
    { mime: "application/vnd.google-apps.script+json", ext: ".json" },
  ],
};

const SKIP_EXPORT_TYPES = new Set<string>([
  "application/vnd.google-apps.form",
  "application/vnd.google-apps.site",
  "application/vnd.google-apps.map",
  "application/vnd.google-apps.fusiontable",
]);

// File types the agent's extractors can actually read. Anything
// else is filtered out before download so we don't burn browser
// memory + S3 budget on bytes the agent will ignore (the typical
// Inrange project folder is dominated by drone imagery, CAD files,
// and video walkthroughs, which today's pypdf + python-docx +
// openpyxl extractors can't open).
const AGENT_READABLE_EXTS = new Set([
  "pdf", "docx", "doc", "xlsx", "xls", "csv", "txt", "png", "jpg", "jpeg",
]);
const AGENT_READABLE_NATIVE_MIMES = new Set([
  // Anything in NATIVE_EXPORT_MAP - we can export these.
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.spreadsheet",
  "application/vnd.google-apps.presentation",
  "application/vnd.google-apps.drawing",
]);

// ---------------------------------------------------------------------- //
// Errors
// ---------------------------------------------------------------------- //

// Soft-skip: the file exists in Drive but isn't downloadable for
// reasons we can't fix from the FE (Forms / Sites / Maps don't
// export; Drawings that exceed every export-format size cap).
// Distinguished from hard failures (network, auth, server 5xx) so
// the concurrent runner can report them separately.
export class DriveSkippedError extends Error {
  readonly skipped = true as const;
  constructor(message: string) {
    super(message);
    this.name = "DriveSkippedError";
  }
}

// ---------------------------------------------------------------------- //
// Picker
// ---------------------------------------------------------------------- //

// Open the Google Picker with two views (files + folders), both
// folder-selectable. `onPicked` fires when the operator confirms;
// the picker auto-closes after. Resolves the returned Promise to
// the picked docs (file/folder metadata) OR null on cancel.
export const openDrivePicker = (accessToken: string): Promise<PickerDoc[] | null> => {
  return new Promise((resolve, reject) => {
    if (!window.google?.picker) {
      reject(new Error("Google Picker SDK not loaded - call ensureGapiPickerLoaded() first."));
      return;
    }
    const picker = window.google.picker;
    const filesView = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true);
    const foldersView = new picker.DocsView(picker.ViewId.FOLDERS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes(FOLDER_MIME);
    const built = new picker.PickerBuilder()
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(accessToken)
      .setDeveloperKey(GOOGLE_API_KEY)
      .addView(filesView)
      .addView(foldersView)
      .setCallback((data) => {
        // Picker emits the PICKED action when the operator
        // confirms; CANCEL when they close without picking. We
        // treat any other action (loaded/etc.) as no-op.
        if (data.action === picker.Action.PICKED) {
          resolve(data.docs ?? []);
        } else if (data.action === picker.Action.CANCEL) {
          resolve(null);
        }
      })
      .build();
    built.setVisible(true);
  });
};

// ---------------------------------------------------------------------- //
// Drive listing
// ---------------------------------------------------------------------- //

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string; // Drive returns this as a string of bytes
  parents?: string[];
  shortcutDetails?: { targetId?: string; targetMimeType?: string };
}

// Direct children only. Returns BOTH files and sub-folders - the
// caller recurses into sub-folders separately.
//
// supportsAllDrives + includeItemsFromAllDrives + corpora=allDrives
// make Shared Drives and shared-with-me folders visible. Without
// them the API silently returns 0 hits for anything outside the
// operator's own My Drive.
//
// `size` lets the preview panel show byte totals + per-file cap
// warnings BEFORE any download starts. Google-native types
// (Docs / Sheets / Slides) don't carry a size in Drive metadata -
// they come back without that field and the classifier falls
// through to `unknown size`.
export const listFolderChildren = async (
  folderId: string,
  accessToken: string,
  signal?: AbortSignal,
): Promise<DriveFile[]> => {
  const params = new URLSearchParams({
    q: `'${folderId}' in parents and trashed=false`,
    fields: "files(id,name,mimeType,parents,shortcutDetails,size)",
    pageSize: "200",
    supportsAllDrives: "true",
    includeItemsFromAllDrives: "true",
    corpora: "allDrives",
  });
  const resp = await fetch(
    `https://www.googleapis.com/drive/v3/files?${params}`,
    { headers: { Authorization: `Bearer ${accessToken}` }, signal },
  );
  if (!resp.ok) {
    throw new Error(`Drive folder list failed (${resp.status}): ${await resp.text()}`);
  }
  const data = (await resp.json()) as { files?: DriveFile[] };
  return data.files ?? [];
};

// Breadth-first walk. depth-capped so a pathological symlink loop
// or massive Drive can't lock the tab; visited-tracked so the same
// folder ID isn't expanded twice. Returns a flat list of files
// (no sub-folders).
//
// `listFn` takes a folder ID and returns its children. The caller
// is expected to bind in the access token + retry-on-401 logic
// once, then pass the bound function here. This keeps the
// retry boundary at the *leaf* of the walk: if a token expires
// halfway through, the retried single page call slots back into
// the walk at exactly the folder it left off in, rather than
// replaying the whole tree from the root. The shared `visited`
// set survives retries because it lives in this function's
// closure, not behind the retry wrapper.
//
// Optional `signal` is checked at the start of each folder
// expansion so Reset mid-walk halts promptly without waiting for
// the in-flight HTTP request to settle.
export const listFolderRecursively = async (
  folderId: string,
  listFn: (folderId: string) => Promise<DriveFile[]>,
  opts?: { depth?: number; visited?: Set<string>; signal?: AbortSignal },
): Promise<DriveFile[]> => {
  const depth = opts?.depth ?? 0;
  const visited = opts?.visited ?? new Set<string>();
  if (opts?.signal?.aborted) throw new DOMException("Aborted", "AbortError");
  if (depth > MAX_FOLDER_DEPTH) return [];
  if (visited.has(folderId)) return [];
  visited.add(folderId);

  const children = await listFn(folderId);
  const files: DriveFile[] = [];
  const subFolders: DriveFile[] = [];
  for (const child of children) {
    if (child.mimeType === FOLDER_MIME) subFolders.push(child);
    else files.push(child);
  }
  for (const sub of subFolders) {
    const nested = await listFolderRecursively(sub.id, listFn, {
      depth: depth + 1,
      visited,
      signal: opts?.signal,
    });
    files.push(...nested);
  }
  return files;
};

// ---------------------------------------------------------------------- //
// Classification
// ---------------------------------------------------------------------- //

export interface ClassifiedDriveFile extends DriveFile {
  _ext: string;
  _sizeNum: number | null;
}

export interface DriveClassification {
  toImport: ClassifiedDriveFile[];
  tooBig: ClassifiedDriveFile[];
  unsupported: ClassifiedDriveFile[];
  bytes: { toImport: number; tooBig: number; unsupported: number };
}

// Three buckets:
//   - toImport: agent-readable AND under cap
//   - tooBig: agent-readable but oversize
//   - unsupported: agent can't read this format
// Google-native types are unsized; assume they're always under
// the cap (their text/PDF export is bounded by Google's own 10 MB
// export limit, which downloadDriveFileAsBlob handles separately).
export const classifyDriveFiles = (
  files: DriveFile[],
  sizeCapMB: number = DEFAULT_SIZE_CAP_MB,
): DriveClassification => {
  const sizeCapBytes = sizeCapMB * 1024 * 1024;
  const toImport: ClassifiedDriveFile[] = [];
  const tooBig: ClassifiedDriveFile[] = [];
  const unsupported: ClassifiedDriveFile[] = [];
  for (const f of files) {
    const ext = (f.name ?? "").toLowerCase().split(".").pop() ?? "";
    const isReadable =
      AGENT_READABLE_EXTS.has(ext) || AGENT_READABLE_NATIVE_MIMES.has(f.mimeType);
    const sizeNum = f.size ? Number(f.size) : null;
    const annotated: ClassifiedDriveFile = { ...f, _ext: ext, _sizeNum: sizeNum };
    if (!isReadable) {
      unsupported.push(annotated);
    } else if (sizeNum != null && sizeNum > sizeCapBytes) {
      tooBig.push(annotated);
    } else {
      toImport.push(annotated);
    }
  }
  const sum = (xs: ClassifiedDriveFile[]): number =>
    xs.reduce((a, x) => a + (x._sizeNum ?? 0), 0);
  return {
    toImport,
    tooBig,
    unsupported,
    bytes: {
      toImport: sum(toImport),
      tooBig: sum(tooBig),
      unsupported: sum(unsupported),
    },
  };
};

export const formatBytesPretty = (n: number): string => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// Per-bucket breakdown helper for the preview UI.
export const countByExt = (files: ClassifiedDriveFile[]): [string, number][] => {
  const counts: Record<string, number> = {};
  for (const f of files) {
    const key =
      f._ext ||
      (f.mimeType || "").replace("application/vnd.google-apps.", "g-") ||
      "unknown";
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1]);
};

// ---------------------------------------------------------------------- //
// Download
// ---------------------------------------------------------------------- //

const resolveShortcut = async (
  doc: DriveFile,
  accessToken: string,
  signal?: AbortSignal,
): Promise<DriveFile> => {
  const resp = await fetch(
    `https://www.googleapis.com/drive/v3/files/${doc.id}?fields=shortcutDetails,name&supportsAllDrives=true`,
    { headers: { Authorization: `Bearer ${accessToken}` }, signal },
  );
  if (!resp.ok) {
    throw new Error(`Shortcut resolve failed: ${await resp.text()}`);
  }
  const data = (await resp.json()) as DriveFile;
  const target = data.shortcutDetails;
  if (!target?.targetId) {
    throw new DriveSkippedError(`shortcut "${doc.name}" has no target`);
  }
  return {
    id: target.targetId,
    name: doc.name,
    mimeType: target.targetMimeType ?? "application/octet-stream",
  };
};

// Download one Drive file as a `File` ready for the existing
// /uploads/initiate pipeline. Three branches:
//   1. Shortcut -> recurse into the target.
//   2. Google-native -> try each export candidate in order,
//      falling through on `exportSizeLimitExceeded`.
//   3. Plain binary -> direct GET with alt=media.
//
// `signal` is forwarded to every fetch so that Reset mid-batch
// cancels the in-flight HTTP request rather than waiting for the
// whole blob to download first.
export const downloadDriveFileAsBlob = async (
  doc: DriveFile,
  accessToken: string,
  signal?: AbortSignal,
): Promise<File> => {
  const mime = doc.mimeType;

  if (mime === "application/vnd.google-apps.shortcut") {
    const target = await resolveShortcut(doc, accessToken, signal);
    return downloadDriveFileAsBlob(target, accessToken, signal);
  }

  if (SKIP_EXPORT_TYPES.has(mime)) {
    throw new DriveSkippedError(
      `"${doc.name}" is a Google ${mime.replace("application/vnd.google-apps.", "")} — type can't be exported`,
    );
  }

  const isNative = mime?.startsWith("application/vnd.google-apps.");
  if (isNative) {
    const candidates = NATIVE_EXPORT_MAP[mime];
    if (!candidates) {
      throw new DriveSkippedError(
        `"${doc.name}" is a Google ${mime.replace("application/vnd.google-apps.", "")} — no export format wired`,
      );
    }
    let lastDetail = "";
    for (const candidate of candidates) {
      const url = `https://www.googleapis.com/drive/v3/files/${doc.id}/export?mimeType=${encodeURIComponent(candidate.mime)}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal,
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const outName = doc.name.toLowerCase().endsWith(candidate.ext)
          ? doc.name
          : `${doc.name}${candidate.ext}`;
        return new File([blob], outName, { type: candidate.mime });
      }
      lastDetail = await resp.text();
      // exportSizeLimitExceeded (403) means the requested format
      // is too big - fall through to the next candidate. Any
      // other error is fatal; break out.
      if (resp.status !== 403 || !lastDetail.includes("exportSizeLimitExceeded")) {
        throw new Error(
          `Drive download failed for "${doc.name}" (${resp.status}): ${lastDetail}`,
        );
      }
    }
    throw new DriveSkippedError(
      `"${doc.name}" exceeded Google's export size limit on every available format — skipped`,
    );
  }

  // Non-native files: direct binary download via alt=media. No
  // export-size limit applies here, but the operator's local
  // Drive quota / file permissions can still fail the call.
  const url = `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media&supportsAllDrives=true`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });
  if (!resp.ok) {
    throw new Error(
      `Drive download failed for "${doc.name}" (${resp.status}): ${await resp.text()}`,
    );
  }
  const blob = await resp.blob();
  return new File([blob], doc.name, { type: mime || "application/octet-stream" });
};

// ---------------------------------------------------------------------- //
// Concurrent runner
// ---------------------------------------------------------------------- //

export interface ConcurrencyProgress<T> {
  done: number;
  total: number;
  lastName?: string;
  lastItem?: T;
}

// Generic worker-pool: run `fn` against `items` with at most
// `concurrency` in flight. Errors per item are collected, never
// thrown, so one broken file doesn't kill the whole batch.
// `onItem` fires after each task completes (used to drive the
// progress bar).
//
// `signal` is honoured in two ways:
//  1. Each worker checks `signal.aborted` between items and bails
//     out promptly rather than starting the next download.
//  2. The signal is also passed to `fn` so per-item fetches can
//     abort in flight (mid-blob-download) rather than running to
//     completion just to discard the result.
// Already-completed items remain in `results`; aborted items go
// into `errors` with an AbortError.
export const runWithConcurrency = async <T, R>(
  items: T[],
  fn: (item: T, signal?: AbortSignal) => Promise<R>,
  opts?: {
    concurrency?: number;
    onItem?: (p: ConcurrencyProgress<T> & { name?: string }) => void;
    signal?: AbortSignal;
  },
): Promise<{ results: R[]; errors: { item: T; error: Error }[]; aborted: boolean }> => {
  const concurrency = opts?.concurrency ?? 6;
  const queue: { idx: number; it: T }[] = items.map((it, idx) => ({ idx, it }));
  const results: (R | null)[] = new Array(items.length).fill(null);
  const errors: { item: T; error: Error }[] = [];
  let done = 0;
  const worker = async (): Promise<void> => {
    while (queue.length > 0) {
      if (opts?.signal?.aborted) return;
      const next = queue.shift();
      if (!next) return;
      const { idx, it } = next;
      try {
        results[idx] = await fn(it, opts?.signal);
      } catch (e) {
        errors.push({ item: it, error: e instanceof Error ? e : new Error(String(e)) });
      }
      done += 1;
      const name =
        typeof it === "object" && it !== null && "name" in it
          ? String((it as { name: unknown }).name ?? "")
          : "";
      opts?.onItem?.({ done, total: items.length, lastItem: it, name });
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length || 1) }, () => worker()),
  );
  return {
    results: results.filter((r): r is R => r !== null),
    errors,
    aborted: opts?.signal?.aborted === true,
  };
};
